import { useErrors } from '@auth0/auth0-acul-react/signup-password';
import BaseWrapper from 'creative-ui/oktaComponents/BaseWrapper';
import Cta from 'creative-ui/uikit/Cta';
import CtaContainer from 'creative-ui/uikit/CtaContainer';
import SvgChevronLeft from 'creative-ui/uikit/svg/chevron-left';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthErrorBanner } from '@/components/AuthErrorBanner/AuthErrorBanner';
import Text from '@/components/Typography/Text/Text';
import { useTrackErrors } from '@/hooks/useTrackErrors';
import { useTransactionLocale } from '@/hooks/useTranslationLocale';
import { trackPageView } from '@/lib/tagging';
import { setCookie } from '@/utils/helpers/cookie';
import {
  getEnvironmentConfig,
  getLoginRedirectUrl,
  getPrivacyPolicyUrl,
} from '@/utils/helpers/environment';

import type { ConsentManifest } from './helpers/consentManifest';
import SignupPasswordForm from './components/SignupPasswordForm';
import TermsAndConditionsForm from './components/TermsAndConditionsForm';
import { fetchConsentManifest } from './helpers/consentManifest';
import { useSignupPasswordManager } from './hooks/useSignupPasswordManager';

type Step = 'password' | 'terms';

function SignupPasswordScreen() {
  const { t } = useTranslation(['signupPassword', 'common']);
  const locale = useTransactionLocale();

  const [currentStep, setCurrentStep] = useState<Step>('password');
  const [password, setPassword] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentVersions, setConsentVersions] =
    useState<ConsentManifest | null>(null);

  const { texts, handleSignup } = useSignupPasswordManager();

  const { errors } = useErrors();

  const { generalErrorMessages, fieldErrors } = useTrackErrors(
    errors,
    'selfactivation/password/error',
  );

  useEffect(() => {
    if (currentStep === 'password') trackPageView('selfactivation/password');
    if (currentStep === 'terms') trackPageView('selfactivation/legalterms');
  }, [currentStep]);

  // Prefetch consent versions when the user reaches the terms step so the
  // manifest is ready synchronously by the time they accept. Fetch is
  // tolerant — null result simply skips the cookie write later.
  useEffect(() => {
    if (currentStep !== 'terms' || consentVersions) return;
    let cancelled = false;
    fetchConsentManifest().then((manifest) => {
      if (!cancelled) setConsentVersions(manifest);
    });
    return () => {
      cancelled = true;
    };
  }, [currentStep, consentVersions]);

  // SDK first, i18n fallback
  const pageTitle = texts?.pageTitle || t('page.title');
  const termsPageTitle =
    texts?.termsPageTitle || t('termsAndConditions.pageTitle');
  document.title = currentStep === 'password' ? pageTitle : termsPageTitle;

  const title = texts?.title || t('header.title');
  const description = texts?.description || t('header.description');
  const termsTitle = texts?.termsTitle || t('termsAndConditions.title');
  const termsDescription =
    texts?.termsDescription || t('termsAndConditions.description');

  const handlePasswordSubmit = (submittedPassword: string) => {
    setPassword(submittedPassword);
    setCurrentStep('terms');
  };

  const handleBackToPassword = () => {
    setCurrentStep('password');
  };

  const handleAcceptTerms = async (captcha: string | undefined) => {
    setIsSubmitting(true);
    // Mirror PP acceptance into a cross-subdomain cookie so the shell SPA
    // does not re-prompt. Skip silently when the manifest could not be
    // fetched — the shell will fall back to the regular prompt.
    const ppVersion = consentVersions?.ppConsentVersion;
    if (ppVersion) {
      setCookie('pp_consent', ppVersion, {
        maxAge: 31536000,
        sameSite: 'Lax',
        domain: getEnvironmentConfig().cookieDomain,
      });
    }
    try {
      await handleSignup(password, captcha);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSignup = () => {
    window.location.href = getLoginRedirectUrl(locale);
  };

  const backButtonText = t('common:button.back');

  if (currentStep === 'terms') {
    return (
      <BaseWrapper>
        <CtaContainer alignment="left" className="u-margin-b-l">
          <Cta
            type="ghost-inline"
            svgLeft={SvgChevronLeft}
            noLabel
            aria-label={backButtonText}
            onClick={handleBackToPassword}>
            {backButtonText}
          </Cta>
        </CtaContainer>
        <h1 className="u-heading-2 u-textCenter u-margin-b-l">{termsTitle}</h1>
        <p className="u-textCenter u-margin-b-l">{termsDescription}</p>
        <TermsAndConditionsForm
          onAccept={handleAcceptTerms}
          onCancel={handleCancelSignup}
          isSubmitting={isSubmitting}
          privacyUrl={getPrivacyPolicyUrl(locale)}
          fieldErrors={fieldErrors}
          texts={texts}
        />
      </BaseWrapper>
    );
  }

  return (
    <BaseWrapper title={title}>
      <Text align="center" className="u-margin-b-l">
        {description}
      </Text>
      <AuthErrorBanner errors={generalErrorMessages} />
      <SignupPasswordForm
        onPasswordSubmit={handlePasswordSubmit}
        fieldErrors={fieldErrors}
      />
    </BaseWrapper>
  );
}

export default SignupPasswordScreen;
